import { useMemo } from 'react';
import { forceVisible } from 'react-lazyload';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Types
import { RootState } from 'app/store';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectDocumentEpisodeTypeOptions, selectEpisodeTypesAsOptions } from 'app/store/Episodes/Episodes.selectors';
import { selectFilter } from 'app/store/page/page.selectors';
// Mui
import { Box, Checkbox } from '@mui/material';
// Components
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import { PagesToolbarActions } from 'app/store/pagesToolbar/pagesToolbar.slice';

const EpisodeTypesSelect = () => {
  const { t } = useTranslation();

  const { documentId } = useParams<{ documentId:string; }>();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const filter = useAppSelector(selectFilter);
  const documentEpisodeTypeOptions = useAppSelector((state:RootState) => selectDocumentEpisodeTypeOptions(state, {
    documentId: +documentId
  })) as (string[] | null);
  // ToDO
  const episodeTypeOptions = useAppSelector(selectEpisodeTypesAsOptions) as any;
  // End: ToDO

  const options = useMemo(() => {
    // if no documentId, it will mean that we are on the workspace page
    if ( !documentEpisodeTypeOptions ) return episodeTypeOptions || [];
    return documentEpisodeTypeOptions;
  }, [episodeTypeOptions, documentEpisodeTypeOptions]);

  const handleChange = (newValue:unknown) => {
    dispatch(PageActions.setFilter('types', newValue as string[]));
    dispatch(PagesToolbarActions.clearSelectedPages());
    forceVisible();
  }

  return (
    <Autocomplete
      sx={{
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          minWidth: 'unset !important',
        }
      }}
      value={filter.types}
      onChange={handleChange}
      options={options}
      TextFieldProps={{
        label: t('labels.types'),
        size: 'small',
        margin: 'none'
      }}
      ListboxProps={{
        sx: {
          // Reduce option horizontal padding
          '& .MuiAutocomplete-option': {
            px: 2
          }
        }
      }}
      multiple
      limitTags={2}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value}>
          <Checkbox sx={{ p: 1, pr: 3 }} checked={filter.types.includes(option.value as string)} size="small" />
          {option.label}
        </Box>
      )}
      ChipProps={{
        sx: { maxWidth: `92px !important` },
        size: 'small'
      }}
      disableCloseOnSelect
    />
  )
}

export default EpisodeTypesSelect;
